import AutoCheckbox from '@liveauctioneers/caterwaul-components/lib/Checkbox/AutoCheckbox';
import Input from '@liveauctioneers/caterwaul-components/lib/Input/Input';
import Radio from '@liveauctioneers/caterwaul-components/lib/Radio/Radio';
import Radios from '@liveauctioneers/caterwaul-components/lib/Radio/Radios';
import Text from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { FormattedMessage, injectIntl, IntlShape } from '@liveauctioneers/hammer-ui-core/intl';
import React, { Component } from 'react';
import isEqual from 'react-fast-compare';
import styled from 'styled-components';

import PageTitle from '@/components/PageTitle/PageTitle';
import ShouldLogInContainer from '@/components/ShouldLogIn/ShouldLogInContainer';
import { EventContext } from '@/redux/modules/analytics';
import { submitConsignment } from '@/redux/modules/consignment';
import { ModalContext } from '@/redux/modules/modal';
import { ConsignmentForm } from '@/types/Consignment';
import { Seller } from '@/types/Seller';
import noop from '@/utils/noop';
import { preventDefault } from '@/utils/preventDefault';
import { required } from '@/utils/validation';

import ConsignmentsPageHeaderMeta from './ConsignmentsPageHeaderMeta';

const defaultState = {
    aquireCode: '',
    aquireCodeValid: true,
    categories: [],
    categoriesValid: true,
    description: '',
    descriptionValid: true,
    file: null,
    other: '',
    photo: null,
    photoValid: true,
};

type Props = {
    baseUrl: string;
    emailAddress: string;
    intl: IntlShape;
    openConfirmConsignmentModal: Function;
    seller: Seller;
    sellerId: number;
    submitConsignment: (form: ConsignmentForm, file: File) => ReturnType<ReturnType<typeof submitConsignment>>;
    submitConsignmentStatus: any;
    userLoggedIn: boolean;
};

type State = {
    aquireCode: string;
    aquireCodeValid: boolean;
    categories: any[];
    categoriesValid: boolean;
    description: string;
    descriptionValid: boolean;
    file: File;
    other: string;
    photo: any;
    photoValid: boolean;
};

const modalContext: ModalContext = {
    eventContext: EventContext.consign,
    id: 0,
};

class ConsignmentsPage extends Component<Props, State> {
    state = defaultState;

    shouldComponentUpdate = (nextProps, nextState) =>
        !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);

    fileUpload: HTMLInputElement | null | undefined;

    onSubmitConsignment = () => {
        const { aquireCode, categories, description, file, other } = this.state;

        let valid = this.onValidating(description, 'descriptionValid');
        valid = valid && this.onValidating(aquireCode, 'aquireCodeValid');
        valid = valid && this.onValidating(categories, 'categoriesValid');
        valid = valid && this.onValidating(file, 'photoValid');

        if (valid) {
            this.props
                .submitConsignment(
                    {
                        description,
                        email: this.props.emailAddress,
                        multi2: categories.join(', '),
                        other: `${other} - ${aquireCode}`,
                    },
                    file
                )
                .then(() => {
                    this.setState(defaultState);
                    return this.props.openConfirmConsignmentModal();
                });
        }
    };

    onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files[0];

        if (file && file.size < 10000000 && (file.type === 'image/jpeg' || file.type === 'image/png')) {
            const reader = new FileReader();
            reader.onload = (upload) => {
                this.setState({
                    file,
                    photo: upload.target.result,
                    photoValid: true,
                });
            };

            reader.readAsDataURL(file);
        } else {
            this.setState({ file: null, photo: null, photoValid: false });
            return;
        }
    };

    onValidating = (value, validField) => {
        let validState = required(value);
        if (validField === 'photoValid') {
            validState = Boolean(value);
        }
        const newState = {};
        newState[validField] = validState;
        this.setState(newState);
        return validState;
    };

    onValueChange = (event) => {
        const newState = {};
        const { id, value } = event.target;
        newState[id] = value;
        this.setState(newState);
        this.onValidating(id, `${id}Valid`);
    };

    onCheckboxChange = ({ currentTarget }) => {
        const { checked, name } = currentTarget;
        const categories = [...this.state.categories];
        if (checked) {
            categories.push(name);
        } else {
            const index = categories.indexOf(name);
            categories.splice(index, 1);
        }
        this.setState({
            categories,
            categoriesValid: Boolean(categories.length),
        });
    };

    handleRadioChange = (event) => {
        const aquireCode = event.currentTarget.id;
        return this.setState({
            aquireCode,
            aquireCodeValid: true,
        });
    };

    render() {
        const { baseUrl, emailAddress, intl, seller, sellerId, submitConsignmentStatus, userLoggedIn } = this.props;

        const { error, submitted } = submitConsignmentStatus;

        const { aquireCodeValid, categoriesValid, description, descriptionValid, other, photoValid } = this.state;

        const { formatMessage } = intl;

        const header = sellerId ? (
            <FormattedMessage
                id="how_to_consign_items_to_seller"
                values={{ sellerName: seller.name }}
            />
        ) : (
            <FormattedMessage id="consign_with_trusted_auction_houses" />
        );
        const consign_1 = sellerId ? (
            <FormattedMessage
                id="consign_description_1_seller"
                values={{
                    sellerName: seller.name,
                    title: (
                        <BoldSpan key="consign-description-1-title">
                            <FormattedMessage id="consign_description_1_title" />
                        </BoldSpan>
                    ),
                }}
            />
        ) : (
            <FormattedMessage id="consign_description_1" />
        );
        const consign_2 = sellerId ? (
            <FormattedMessage
                id="consign_description_2_seller"
                values={{
                    sellerName: seller.name,
                    title: (
                        <BoldSpan key="consign-description-2-title">
                            <FormattedMessage id="consign_description_2_title" />
                        </BoldSpan>
                    ),
                }}
            />
        ) : (
            <FormattedMessage id="consign_description_2" />
        );

        return (
            <Content>
                <ConsignmentsPageHeaderMeta baseUrl={baseUrl} />
                <TopWrapper>
                    <PageTitle title={header} />
                    <ConsignmentsContent>
                        <p>{consign_1}</p>
                        <p>{consign_2}</p>
                        <p>
                            <FormattedMessage
                                id="consign_description_3"
                                values={{
                                    title: (
                                        <BoldSpan key="consign-description-3-title">
                                            <FormattedMessage id="consign_description_3_title" />
                                        </BoldSpan>
                                    ),
                                }}
                            />
                        </p>
                        <StyledForm onSubmit={preventDefault}>
                            {userLoggedIn && (
                                <Input
                                    aria-label={formatMessage({
                                        id: 'email_address',
                                    })}
                                    disabled
                                    id="email"
                                    label={formatMessage({
                                        id: 'email_address',
                                    })}
                                    onChange={noop}
                                    type="text"
                                    value={emailAddress}
                                />
                            )}
                            <Input
                                aria-label={formatMessage({
                                    id: 'general_description',
                                })}
                                errorMessage={
                                    <FormattedMessage
                                        id="enter_a_valid"
                                        values={{
                                            token: formatMessage({
                                                id: 'description',
                                            }),
                                        }}
                                    />
                                }
                                help={
                                    <StyledHelp>
                                        (
                                        <FormattedMessage id="description_help" />)
                                    </StyledHelp>
                                }
                                id="description"
                                label={formatMessage({
                                    id: 'general_description',
                                })}
                                onChange={this.onValueChange}
                                testid="generalDescriptionInput"
                                type="textarea"
                                valid={descriptionValid}
                                value={description}
                            />
                            <FormGroup>
                                <Text
                                    colorPrimaryWeak
                                    uppercase
                                >
                                    <FormattedMessage id="how_did_you_aquire" />
                                </Text>
                                <StyledRadiosWrapper>
                                    <Radios onClick={this.handleRadioChange}>
                                        <StyledRadio
                                            label={<FormattedMessage id="i_bought_it" />}
                                            name="I aquired this item through a purchase"
                                            testid="boughtItRadio"
                                        />
                                        <StyledRadio
                                            label={<FormattedMessage id="i_inherited_it" />}
                                            name="I inherited this item"
                                        />
                                        <StyledRadio
                                            label={<FormattedMessage id="i_found_it" />}
                                            name="I found this item"
                                        />
                                        <StyledRadio
                                            label={<FormattedMessage id="it_was_a_gift_it" />}
                                            name="I was given this item"
                                        />
                                    </Radios>
                                    {!aquireCodeValid && (
                                        <StyledError>
                                            <FormattedMessage
                                                id="enter_a_valid"
                                                values={{
                                                    token: formatMessage({
                                                        id: 'value',
                                                    }).toLowerCase(),
                                                }}
                                            />
                                        </StyledError>
                                    )}
                                </StyledRadiosWrapper>
                            </FormGroup>
                            <FormGroup>
                                <Text
                                    colorPrimaryWeak
                                    uppercase
                                >
                                    <FormattedMessage id="category" />
                                </Text>
                                <CheckBoxesContainer>
                                    <section data-testid="20thCenturyCheckbox">
                                        <StyledCheckbox
                                            label={formatMessage({
                                                id: 'twentieth_century',
                                            })}
                                            name="20th Century"
                                            onChange={this.onCheckboxChange}
                                        />
                                    </section>

                                    <StyledCheckbox
                                        label={formatMessage({
                                            id: 'autographs',
                                        })}
                                        name="Autographs"
                                        onChange={this.onCheckboxChange}
                                    />

                                    <StyledCheckbox
                                        label={formatMessage({ id: 'books' })}
                                        name="Books"
                                        onChange={this.onCheckboxChange}
                                    />

                                    <StyledCheckbox
                                        label={formatMessage({
                                            id: 'commercial',
                                        })}
                                        name="Commercial"
                                        onChange={this.onCheckboxChange}
                                    />

                                    <StyledCheckbox
                                        label={formatMessage({
                                            id: 'decorative_arts',
                                        })}
                                        name="Decorative Arts"
                                        onChange={this.onCheckboxChange}
                                    />

                                    <StyledCheckbox
                                        label={formatMessage({
                                            id: 'entertainment',
                                        })}
                                        name="Entertainment"
                                        onChange={this.onCheckboxChange}
                                    />

                                    <StyledCheckbox
                                        label={formatMessage({
                                            id: 'fine_art',
                                        })}
                                        name="Fine Art"
                                        onChange={this.onCheckboxChange}
                                    />

                                    <StyledCheckbox
                                        label={formatMessage({
                                            id: 'navigation.furniture',
                                        })}
                                        name="Furniture"
                                        onChange={this.onCheckboxChange}
                                    />

                                    <StyledCheckbox
                                        label={formatMessage({
                                            id: 'rugs_and_carpets',
                                        })}
                                        name="Rugs/Carpets"
                                        onChange={this.onCheckboxChange}
                                    />

                                    <StyledCheckbox
                                        label={formatMessage({ id: 'toys' })}
                                        name="Toys"
                                        onChange={this.onCheckboxChange}
                                    />

                                    <StyledCheckbox
                                        label={formatMessage({ id: 'other' })}
                                        name="Other"
                                        onChange={this.onCheckboxChange}
                                    />
                                </CheckBoxesContainer>
                                {!categoriesValid && (
                                    <StyledError>
                                        <FormattedMessage
                                            id="enter_a_valid"
                                            values={{
                                                token: formatMessage({
                                                    id: 'category',
                                                }).toLowerCase(),
                                            }}
                                        />
                                    </StyledError>
                                )}
                            </FormGroup>
                            <Input
                                aria-label={`${formatMessage({
                                    id: 'other',
                                })} ${formatMessage({
                                    id: 'optional_parenthesis',
                                })}`}
                                errorMessage={
                                    <FormattedMessage
                                        id="enter_a_valid"
                                        values={{
                                            token: formatMessage({
                                                id: 'other',
                                            }),
                                        }}
                                    />
                                }
                                help={
                                    <StyledHelp>
                                        (<FormattedMessage id="other_help" />)
                                    </StyledHelp>
                                }
                                id="other"
                                label={`${formatMessage({
                                    id: 'other',
                                })} ${formatMessage({
                                    id: 'optional_parenthesis',
                                })}`}
                                labelDisplay={
                                    <span>
                                        <FormattedMessage id="other" />
                                        &nbsp;
                                        <span
                                            style={{
                                                textTransform: 'lowercase',
                                            }}
                                        >
                                            <FormattedMessage id="optional_parenthesis" />
                                        </span>
                                    </span>
                                }
                                onChange={this.onValueChange}
                                type="textarea"
                                value={other}
                            />
                            <Input
                                // @ts-expect-error accept is handled by rest
                                accept=".jpg, .png"
                                aria-label={formatMessage({ id: 'photo' })}
                                errorMessage={<FormattedMessage id="please_choose_a_photo" />}
                                id="photo"
                                label={formatMessage({ id: 'photo' })}
                                onChange={this.onFileChange}
                                type="file"
                                valid={photoValid}
                            />
                            {Boolean(this.state.photo) && <StyledImagePreview src={this.state.photo} />}
                            <ShouldLogInContainer
                                modalContext={modalContext}
                                requireCompleteProfile
                                requireLogin
                                runFunction={this.onSubmitConsignment}
                            >
                                <SubmitButton
                                    data-testid="submitButton"
                                    loading={submitted}
                                    style="primary"
                                    type="submit"
                                >
                                    <FormattedMessage id="submit" />
                                </SubmitButton>
                            </ShouldLogInContainer>
                            {error && (
                                <StyledError>
                                    <FormattedMessage id="there_was_an_error_submitting_consignment" />
                                </StyledError>
                            )}
                        </StyledForm>
                    </ConsignmentsContent>
                </TopWrapper>
            </Content>
        );
    }
}

export default injectIntl(ConsignmentsPage);

const Content = styled.div`
    max-width: 640px;
    margin: 0 auto;
    padding: 0 20px 30px;
`;

const TopWrapper = styled.div`
    transition: max-height 1.5s;
    max-height: 5000px;

    &.hide {
        max-height: 0;
        overflow: hidden;
    }

    h2 {
        padding-top: 30px;
        padding-bottom: 18px;
    }

    h1 {
        text-align: center;
    }
`;

const ConsignmentsContent = styled.div`
    p {
        text-align: center;
        margin-bottom: 30px;
    }
`;

const StyledForm = styled.div`
    display: flex;
    flex-flow: column wrap;
    justify-content: flex-start;
    align-content: stretch;
    align-items: stretch;
    margin: 20px 0 0 20px;
    padding: 0;
    max-width: 640px;
`;

const FormGroup = styled.div`
    margin-bottom: 24px;
    label[class*='control-label'] {
        font-weight: normal;
        color: ${({ theme }) => theme.colors.grey100};
        font-size: 16px;
        line-height: 1;
        letter-spacing: 1px;
        text-transform: uppercase;
        margin-bottom: 4px;
        margin-top: 5px;
    }
`;
const StyledRadio = styled(Radio)`
    margin: 0;

    label {
        padding: 0;
        margin: 0 0 4px;

        input {
            position: relative;
            margin: 0 10px 0 1px;
            height: 14px;
            width: 14px;
        }

        span {
            letter-spacing: 0.5px;
            color: ${({ theme }) => theme.colors.grey100};
            font-size: 12px;
            vertical-align: top;
            display: inline-block;
            line-height: 14px;
        }

        @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
            margin: 0 0 18px;
        }
    }
`;

const StyledRadiosWrapper = styled.div`
    margin-top: 20px;
`;

const CheckBoxesContainer = styled.div`
    display: flex;
    flex-flow: wrap;
    margin-top: 20px;
`;

const StyledCheckbox = styled(AutoCheckbox)`
    margin-right: 25px;
`;

const StyledHelp = styled.span`
    font-size: 14px;
`;

const StyledError = styled.div`
    color: ${({ theme }) => theme.colors.red300};
`;

const SubmitButton = styled(Button)`
    min-width: 160px;
    margin-bottom: 15px;
`;

const StyledImagePreview = styled.img`
    max-width: 640px;
    margin-bottom: 30px;
`;

const BoldSpan = styled.span`
    font-family: ${({ theme }) => theme.typography.fontPrimaryStrong};
`;
